import { DateFormat, TimeFormat } from "shared/utils";
import {
  IAddress,
  IBaseInfoBlock,
  IBasicAddress,
  IDataKeys,
  IFile,
  IPermission,
  IPermissionForKey,
} from "shared/types";
import { ISelectOption } from "shared/components/Form/Fields/select/types";
import { IActionDropdownItem, IFileUploadValue } from "shared/components";
import { IStage, IStageCapacity, IVenue } from "Venues/types";
import { IBandUser } from "./user";

export interface IBand {
  id: string;
  title: string;
  timeZone?: string;
  dateFormat?: DateFormat;
  timeFormat?: TimeFormat;
  users?: IBandUser[];
  eventTypes?: IIconBandSetting[];
  ticketAndPassTypes?: IBaseBandSetting[];
  eventInfoBlockTypes?: IIconBandSetting[];
  infoBlockTypes?: IIconBandSetting[];
  memberCategories?: IBaseBandSetting[];
  subscription?: IBandSubscription;
  createdAt: string;
  permissions?: IPermissionForKey[];
}

export interface IBandPatchData {
  title?: string;
  timeZone?: string;
  dateFormat?: string;
  timeFormat?: string;
}

export enum IEventStatus {
  confirmed = "confirmed",
  pending = "pending",
  onHold = "on_hold",
  postponed = "postponed",
  canceled = "canceled",
}

export interface IEvent {
  id: string;
  title: string;
  band: string | Pick<IBand, "id"> | IBand;
  bandName?: string;
  date: string;
  time?: string | null;
  timeZone?: string;
  type: string | IIconBandSetting;
  status: IEventStatus;
  description?: string;
  permissions?: IPermissionForKey[];
  eventInfoBlockTypes?: IIconBandSetting[];
  userAttendance?: PeopleBlockStatus;
  lastMailedDetailsExport?: {
    id: string;
  };
  nextEvent?: {
    id: string;
  };
  previousEvent?: {
    id: string;
  };
}

export interface IBaseBandSetting {
  id: string;
  label: string;
  permissions?: IPermissionForKey[];
}

export interface IIconBandSetting extends IBaseBandSetting {
  icon: string;
}

export type IEditItem = IBaseBandSetting | IIconBandSetting;

export enum SubscriptionType {
  monthly = "monthly",
  yearly = "yearly",
  custom = "custom",
}

export enum SubscriptionTypeWithTrial {
  monthly = "monthly",
  yearly = "yearly",
  custom = "custom",
  trial = "trial",
}

export enum SubscriptionStatus {
  active = "active",
  inactive = "inactive",
}

export const SubscriptionStatusWeight: {
  [key in keyof typeof SubscriptionStatus]: number;
} = {
  [SubscriptionStatus.active]: 0,
  [SubscriptionStatus.inactive]: 1,
};

export enum BillingStatus {
  automatically_renewed = "automatically_renewed",
  not_automatically_renewed = "not_automatically_renewed",
}
export interface ISubscription {
  title: string;
  plan: SubscriptionType;
  amount?: number;
  billingTerm?: string;
  info: string[];
}

export interface IBandSubscriptionParams {
  plan: SubscriptionType;
  bands: string[];
  company: string;
  successUrl: string;
  cancelUrl: string;
}

export interface ICustomSubscriptionRequestParams {
  bands: string[];
  company: string;
  createdBy?: string;
}

export interface IEditBandSubscriptionParams {
  plan: SubscriptionType;
  subscriptionId: string;
}

export interface IBandSubscriptionRequest {
  paymentUrl: string | null;
}

export interface IBandSubscription {
  id: string;
  status: SubscriptionStatus;
  plan: SubscriptionTypeWithTrial;
  billingStatus: BillingStatus;
  start: string;
  stop: string;
  nextBillingDate: string | null;
  nextPhasePlan: SubscriptionTypeWithTrial | null;
  nextPhaseStart: string | null;
  company: Partial<ICompany>;
  bands: Partial<IBand>[];
  paymentMethod: string | null;
  createdBy: {
    id: string;
    firstname: string;
    lastname: string;
  };
  deactivatedAt?: string;
  permissions?: IPermissionForKey[];
}

export interface IBandCustomSubscriptionRequest {
  id: string;
  company: Partial<ICompany>;
  bands: Partial<IBand>[];
  createdBy: {
    id: string;
    firstname: string;
    lastname: string;
  };
}

export interface ICustomSubscription {
  id: string;
  plan: SubscriptionType;
  amount: number;
  start: string;
  stop: string;
  invoiceUrl: string;
  invoiceNumber: string;
  invoiceDate: string;
  permissions?: IPermissionForKey[];
}

export interface ICustomSubscriptionParams {
  plan?: SubscriptionType;
  amount?: number;
  start?: string;
  stop?: string;
  invoiceUrl?: string | null;
  invoiceNumber?: string;
  invoiceDate?: string;
  subscriptionRequest?: string;
}

export interface ICustomSubscriptionPatchParams {
  invoice: {
    amount?: number;
    invoiceUrl?: string | null;
    invoiceNumber?: string;
    invoiceDate?: string;
  };
}

export interface IBandWithSubscriptions extends IBand {
  subscriptions: IBandSubscription[];
  currentSubscription?: IBandSubscription;
}

export interface ICompany {
  id: string;
  name: string;
  billingAddress: IBasicAddress;
  vat?: string;
  permissions?: IPermissionForKey[];
}

export interface ICompanyPatchData {
  name?: string;
  billingAddress?: IBasicAddress;
  vat?: string;
}

export enum CompanyStatusPageType {
  paymentInfo = "paymentInfo",
}

export interface IInvoice {
  id: string;
  amount: number;
  band: IBand;
  date: string;
  invoiceNumber: string;
  paymentMethod: string;
  invoiceUrl: string | null;
}

export type BandSettingsDataKeys =
  | IDataKeys.eventInfoBlockTypes
  | IDataKeys.eventTypes
  | IDataKeys.eventTicketAndPassTypes
  | IDataKeys.memberCategories;

export enum BandSettingsSlugs {
  event_types = "event_types",
  event_ticket_and_pass_types = "event_ticket_and_pass_types",
  event_info_block_types = "event_info_block_types",
  band_member_categories = "band_member_categories",
}

export enum BandSettingsType {
  eventType = "eventType",
  eventTicketAndPassType = "eventTicketAndPassType",
  eventInfoBlockType = "eventInfoBlockType",
  bandMemberCategory = "bandMemberCategory",
}

export interface IBirthdayEvent {
  id: string;
  date: string;
  firstname: string;
  lastname: string;
  nickname?: string;
  band?: string;
  bands?: string[];
}

export interface IBirthdays {
  [month: string]: IBirthdayEvent[];
}

export interface IEventBlock {
  id: string;
  sidemenuId: string;
  title: string;
  event: IEvent;
  permissions?: IPermissionForKey[];
  path: string;
  icon?: React.ComponentType<any>;
  handleEdit: () => void;
  handleEditTitle?: (props: { id: string; title: string }) => void;
  editTitleAction?: string;
  handleDelete: (id: string) => void;
  deleteAction: string;
  handleDownloadGuestlist?: () => void;
  type: EventBlocksType;
  actions?: IActionDropdownItem[];
}

export interface IEventBlockParams {
  title?: string;
  event?: string;
}

export enum EventUniqueBlocks {
  people = "eventPeopleBlock",
  venue = "eventVenueBlock",
}

export enum EventBlocks {
  schedule = "eventScheduleBlock",
  infoBlock = "eventInfoBlock",
  infoBlockType = "eventInfoBlockType",
  guestlist = "eventGuestlistBlock",
  travel = "eventTravelBlock",
  accommodation = "eventAccommodationBlock",
}

export type EventBlocksType = EventUniqueBlocks | EventBlocks;

export interface IEventPeopleBlock extends IEventBlock {
  users?: IPeopleBlockUser[];
}

export interface IEventPeopleBlockParams extends IEventBlockParams {
  users?: IPeopleBlockUser[];
}

export interface IPeopleBlockUser {
  id: string;
  rsvp?: IPeopleBlockRSVP | null;
  status: PeopleBlockStatus;
  user: IBandUser;
  updatedAt: string;
  updates?: IPeopleBlockUserUpdate[];
}

export interface IPeopleBlockUserUpdate {
  id: string;
  date: string;
  action: string;
  user: Pick<IBandUser, "id" | "firstname" | "lastname">;
}

export enum PeopleBlockStatus {
  "attending" = "attending",
  "maybe_attending" = "maybe_attending",
  "not_attending" = "not_attending",
  "rsvp" = "rsvp",
  "none" = "none",
}

export interface IPeopleBlockRSVP {
  id: string;
  event: Partial<IEvent>;
  invitedBy: string;
}

export interface IPeopleBlockFormData {
  users: ISelectOption[];
  status: PeopleBlockStatus;
}

export interface IPeopleBlockParams {
  users?: string[];
  status?: PeopleBlockStatus;
  rsvpUrl?: string;
}

export interface IScheduleBlock extends IEventBlock {
  entries: IScheduleBlockEntry[];
}

export interface IScheduleBlockEntry {
  id: string;
  title: string;
  description?: string;
  start: string;
  end: string;
  startTimezone: string;
  endTimezone: string;
  users?: IBandUser[];
}

export interface IScheduleBlockEntryParams {
  title?: string;
  scheduleBlock?: string;
  description?: string;
  start?: string;
  end?: string;
  startTimezone?: string;
  endTimezone?: string;
  users?: string[];
}

export interface IScheduleBlockEntryProperties {
  title?: string;
  scheduleBlock?: string;
  description?: string;
  startDate?: string;
  startTime?: string;
  startTimezone?: string;
  endDate?: string;
  endTime?: string;
  endTimezone?: string;
  users?: ISelectOption[];
}

export interface IEventInfoBlockWithoutType extends IBaseInfoBlock {}

export interface IEventInfoBlock
  extends Omit<IBaseInfoBlock, "type">,
    Omit<IEventBlock, "type"> {
  type: IIconBandSetting;
}

export interface IEventInfoBlockParams
  extends Omit<IBaseInfoBlock, "id" | "type">,
    IEventBlockParams {
  type: string;
}

export interface IAPIEventInfoBlock
  extends Omit<IBaseInfoBlock, "type">,
    Omit<IEventBlock, "type"> {
  type: {
    type: string;
    icon: string;
  };
}

export interface IVenueBlock extends IEventBlock {
  venue: IVenue;
  stage?: IStage | null;
  stageCapacity?: IStageCapacity | null;
}

export interface IEventVenueBlockParams extends IEventBlockParams {
  venue: string;
  stage?: string | null;
  stageCapacity?: string | null;
}

export interface IGuestlistBlock extends IEventBlock {
  status: IGuestlistBlockStatus;
  guestBlockType: IGuestlistBlockType;
  ticketAndPassTypes: IGuestlistTicketAndPass[];
  maxTicketsPerEntry: number;
  printNote?: string;
  permissions?: IPermissionForKey[];
  lastMailedDetailsExport?: IEventDetailsExport;
}

export interface IGuestlistBlockParams extends IEventBlockParams {
  status?: IGuestlistBlockStatus;
  guestBlockType?: IGuestlistBlockType;
  ticketAndPassTypes?: IGuestlistTicketAndPassParams[];
  maxTicketsPerEntry?: number;
}

export enum IGuestlistBlockStatus {
  open = "open",
  closed = "closed",
}

export enum IGuestlistBlockType {
  public = "public",
  approval_needed = "approval_needed",
  private = "private",
}

export interface IGuestlistTicketAndPass {
  id: string;
  label: string;
  ticketAndPassType: IBaseBandSetting;
  ticketsTotal?: number | null;
  ticketsAvailable?: number;
  ticketsDistributed?: number;
  isUnlimited?: boolean;
  permissions?: IPermissionForKey[];
}

export interface IGuestlistTicketAndPassParams {
  id?: string;
  label?: string;
  guestListBlock?: string;
  ticketAndPassType?: string;
  ticketsTotal?: number | null;
  isUnlimited?: boolean;
}

export enum IEventGuestStatus {
  approved = "approved",
  pending = "pending",
  denied = "denied",
}

export interface IEventGuest {
  id: string;
  firstname: string;
  lastname: string;
  nbGuests: number;
  ticketAndPassTypes: IBaseBandSetting[];
  status: IEventGuestStatus;
  source?: string | null;
  addedBy: IBandUser;
  createdAt: string;
  permissions?: IPermissionForKey[];
  blockId?: string;
}

export interface IEventGuestParams {
  guestListBlock?: string;
  ticketAndPassTypes?: string[];
  firstname?: string;
  lastname?: string;
  nbGuests?: number;
  status?: IEventGuestStatus;
  source?: string | null;
}

export interface IEventTravelBlock extends IEventBlock {
  travelLegs: EventTravelLegType[];
}

export interface IEventTravelBlockParams extends IEventBlockParams {}

export type EventTravelLegType =
  | IEventFlightTravelLeg
  | IEventBusTravelLeg
  | IEventTrainTravelLeg
  | IEventOtherTravelLeg;

export interface IDepartureArrival {
  location: { data: { id: string; attributes: IEventSuggestedLocation } };
  date: string;
  time: string;
  timeZone: string;
}

export interface IDepartureArrivalParams {
  location?: string;
  date?: string;
  time?: string;
  timeZone?: string;
}

interface IBaseTravelLeg {
  id: string;
  type: string;
  departure: IDepartureArrival;
  arrival: IDepartureArrival;
  info?: string;
  personalBookingTicket?: ITravelBlockBookingTicket;
  hasBookingReferencesOrTickets?: boolean;
  permissions?: IPermissionForKey[];
}

export interface IScheduledFlight {
  carrier: {
    iata: string;
    icao: string;
    name: string;
  };
  flightNumber: string;
  departureAirport: {
    name: string;
    eventSuggestedLocationIri: string;
    timeZoneRegionName: string;
    eventSuggestedLocation: IEventSuggestedLocation;
  };
  arrivalAirport: {
    name: string;
    eventSuggestedLocationIri: string;
    timeZoneRegionName: string;
    eventSuggestedLocation: IEventSuggestedLocation;
  };
  departureTime: string;
  arrivalTime: string;
}

interface IBaseTravelLegParams {
  id?: string;
  type?: string;
  travelBlock?: string;
  departure?: IDepartureArrivalParams;
  arrival?: IDepartureArrivalParams;
  info?: string;
}

export interface IEventFlightTravelLeg extends IBaseTravelLeg {
  flightNumber?: string;
  airline?: string;
}

export interface IEventFlightTravelLegParams extends IBaseTravelLegParams {
  flightNumber?: string;
  airline?: string;
}

export interface IEventBusTravelLeg extends IBaseTravelLeg {
  carrierNumber?: string;
  company?: string;
}

export interface IEventBusTravelLegParams extends IBaseTravelLegParams {
  carrierNumber?: string;
  company?: string;
}

export interface IEventTrainTravelLeg extends IEventBusTravelLeg {}

export interface IEventTrainTravelLegParams extends IEventBusTravelLegParams {}

export interface IEventOtherTravelLeg extends IBaseTravelLeg {
  transportMode: string;
}

export interface IEventOtherTravelLegParams extends IBaseTravelLegParams {
  transportMode?: string;
}

export type IEventTravelLegParams = IEventFlightTravelLegParams &
  IEventBusTravelLegParams &
  IEventTrainTravelLegParams &
  IEventOtherTravelLegParams;

export enum TravelLegType {
  flight = "eventFlightTravelLeg",
  bus = "eventBusTravelLeg",
  train = "eventTrainTravelLeg",
  other = "eventOtherTravelLeg",
}

export interface ITravelBlockBookingTicket {
  id: string;
  travelLeg: { id: string; type: string };
  bookingReference: string;
  files: IFile[];
}

export interface ITravelBlockBookingTicketParams {
  travelLeg?: string;
  bookingReference?: string | null;
  files?: { url?: string | null; band?: string }[] | string[];
}

export interface ITravelBlockPassenger {
  id: string;
  user: IBandUser;
  bookingTickets: ITravelBlockBookingTicket[];
  permissions?: IPermissionForKey[];
}

export interface ITravelBlockPassengerFormParams {
  user: string;
  bookingReference?: { [key: string]: string | null | undefined };
  files?: { [key: string]: IFileUploadValue | null };
}

export interface ITravelBlockPassengerParams {
  user?: string;
  travelBlock?: string;
  travelLeg?: (string | null | undefined)[];
  bookingReference?: (string | null | undefined)[];
  files?: { file: { url?: string | null } }[];
  bookingTickets?: ITravelBlockBookingTicketParams[];
}

export interface IEventSuggestedLocation {
  id: string;
  location: string;
  secondaryText: string;
  formattedAddress: string;
  latitude: number;
  longitude: number;
  event?: Pick<
    IEvent,
    "id" | "title" | "date" | "time" | "timeZone" | "status"
  >;
}

export interface IBandIcal {
  id: string;
  title: string;
  eventTypes?: { id: string }[];
  eventStatuses?: string[];
  eventRSVPStatuses?: string[];
  eventContent: string[];
  showBirthdays: boolean;
  splitSchedule: boolean;
}

export interface IBandIcalParams {
  title?: string;
  eventTypes?: string[];
  eventStatuses?: string[];
  eventRsvpStatuses?: string[];
  eventRSVPStatuses?: string[];
  eventContent?: string[];
  showBirthdays?: boolean;
  splitSchedule?: boolean;
}

export interface IBandXlsParams {
  band: string;
  exportType: "excel";
  config: IBandXlsConfigParams;
}

export interface IBandXlsConfigParams {
  startDate?: string;
  endDate?: string;
  eventStatuses?: string[];
  eventTypes?: string[];
  eventRSVPStatuses?: string[];
  eventBlocks?: (string | { [key in `${EventBlocksType}`]: string })[] | any; // TODO fix
}

export interface IAccommodationBlock extends IEventBlock {
  checkInDate?: string;
  checkInTime?: string;
  checkOutDate?: string;
  checkOutTime?: string;
  accommodationName: string;
  address: IAddress;
  bookingId?: string;
  accommodationType: string;
  amenities?: string[];
}
export interface IAccommodationBlockParams extends IEventBlockParams {
  checkInDate?: string;
  checkInTime?: string;
  checkOutDate?: string;
  checkOutTime?: string;
  accommodationName?: string;
  address?: IAddress;
  bookingId?: string;
  accommodationType?: string;
  amenities?: string[];
}

export enum AccommodationType {
  hotel = "Hotel",
  airbnb = "AirBnb",
  hostel = "Hostel",
  house = "House",
  dorm = "Dorm",
}

export enum AmenityType {
  wifi = "Wi-Fi",
  sauna = "Sauna",
  bar = "Bar",
  gym = "Gym",
  washing_service = "Washing service",
}

export interface IAccommodationRoomType {
  id: string;
  accommodationBlock: string;
  name: string;
  quantity: number;
  peoplePerRoom: number;
  vacancy: number;
  minimumQuantity: number;
  minimumPeoplePerRoom: number;
}

export interface IAccommodationRoomWithBlock
  extends Omit<IAccommodationRoomType, "accommodationBlock"> {
  accommodationBlock: { id: string; type: string };
}

export interface IAccommodationRoomTypeParams {
  id?: string;
  accommodationBlock?: string;
  name?: string;
  quantity?: number;
  peoplePerRoom?: number;
}

export enum AccommodationRoomType {
  single = "Single",
  double = "Double",
  triple = "Triple",
  quad = "Quad",
  queen = "Queen",
  king = "King",
  twin = "Twin",
  doubleDouble = "Double-double",
  studio = "Studio",
  masterSuite = "Master Suite",
  juniorSuite = "Junior Suite",
}

export interface IAccommodationReservation {
  id: string;
  accommodationBlock: string;
  bandUser: IBandUser;
  roomNumber: string;
  remark: string;
  room?: {
    id: string;
  };
  roomType?: {
    id: string;
  };
}

export interface IAccommodationReservationWithBlock
  extends Omit<IAccommodationReservation, "accommodationBlock"> {
  accommodationBlock: { id: string };
}

export interface IAccommodationReservationParams {
  people?: string[];
  accommodationBlock?: string;
  roomType?: string;
  room?: string;
}

export interface IAccommodationReservationFormParams {
  people?: ISelectOption[];
  accommodationBlock?: string;
  roomType?: string;
  room?: string;
}

export interface IAccommodationSingleReservationFormParams {
  people?: string;
  roomType?: string;
  room?: string;
  remark?: string;
}

export interface IAccommodationSingleReservationParams {
  id?: string;
  people?: string;
  accommodationBlock?: string;
  roomType?: string;
  room?: string | null;
  remark?: string;
}

export interface IAccommodationRoom {
  id: string;
  roomType: {
    id: string;
    type: string;
  };
  vacancy: number;
  people: IBandUser[];
  permissions: IPermission[];
  roomNumber: string;
}

export interface IEventDetailsExportParams {
  event: string;
  eventBlocks?: string[];
  description?: string;
  title?: string;
  showMyPersonalInfo?: boolean;
  showMyPersonalSchedule?: boolean;
  paperSize?: string;
  membersToMail?: string[] | ISelectOption[];
  externalsToMail?: string[];
}

export interface IEventDetailsExportConfig
  extends Omit<Omit<IEventDetailsExportParams, "event">, "eventBlocks"> {
  event: { id: string };
  band?: IBand;
  eventBlocks: IEventBlock[];
  intro?: string;
}

export enum PDFStatus {
  pending = "pending",
  failed = "failed",
  completed = "completed",
}
export interface IEventDetailsExport {
  id: string;
  sentAt: string;
  createdBy: {
    id: string;
  };
}

export interface IEmailGuestlistBlockParams {
  event: string;
  eventBlocks?: string[];
  description?: string;
  title?: string;
  showMyPersonalInfo?: boolean;
  paperSize?: string;
  membersToMail?: string[] | ISelectOption[];
  externalsToMail?: string[];
}
